import { getAppNames, getAppStatus, MOUNTED, MOUNTING, UNMOUNTING } from 'single-spa';

import {
  Appender,
  LoggerOptions,
  LogEntry,
  Logger
} from '../../types/LoggerTypes';
import {LogLevel} from "@mosaic/logger-service";


/**
 * StandardLogger is the default logger implementation.
 * It collects logger options and forwards created log entries to bound appenders.
 */
export default class StandardLogger implements Logger {
  private options: LoggerOptions
  private appenders: Appender[] = []
  private appendersThreshold: Map<string, LogLevel> = new Map();
  private initialized = false

  /**
   * Create a StandardLogger instance.
   * @param options logger options including component name and level
   */
  constructor(options: LoggerOptions) {
    this.options = options
  }

  /**
   * Bind appenders to this logger and cache their thresholds.
   * After binding the logger becomes active and will emit entries to appenders.
   * @param appenders array of Appender instances
   */
  bind(appenders: Appender[]): void {
    this.appenders = appenders;
    appenders.forEach(appender => {
      this.appendersThreshold.set(appender.name(), appender.getThreshold());
    });
    this.initialized = true;
  }

  /** Log a debug message. */
  public debug(message: string, ...args: any[]) {
    this.log(this.createLogEntry(LogLevel.DEBUG, message, ...args));
  }
  /** Log an info message. */
  public info(message: string, ...args: any[]) {
    this.log(this.createLogEntry(LogLevel.INFO, message, ...args));
  }
  /** Log a warning message. */
  public warn(message: string, ...args: any[]) {
    this.log(this.createLogEntry(LogLevel.WARN, message, ...args));
  }
  /** Log an error message. */
  public error(message: string, ...args: any[]) {
    this.log(this.createLogEntry(LogLevel.ERROR, message, ...args));
  }

  /**
   * Create a LogEntry and forward it to all bound appenders if the logger is
   * initialized and levels permit emission.
   * @param entry log entry to emit
   */
  private log(entry: LogEntry) {
    if (!this.initialized) return;
    if (!this.shouldLog(entry.level, this.options.level)) return;
    const prefix = `@${this.options.component}/`;
    const mfe = getAppNames()
      .filter((name) => name.startsWith(prefix))
      .filter((name) => this.isEmittingStatus(getAppStatus(name)))
      .join(',');
    if (mfe) entry.context = { mfe };
    // emit the event to all configured appenders
    (this.appenders ?? []).forEach((appender: Appender) => {
      const configuredLevel: LogLevel = this.appendersThreshold.get(appender.name()) ?? LogLevel.DEBUG;
      if (this.shouldLog(entry.level, configuredLevel)) appender.append(entry);
    });
  }

  private isEmittingStatus(status: string | null): boolean {
    return status === MOUNTING || status === MOUNTED || status === UNMOUNTING;
  }

  /**
   * Check if a message at requestedLevel should be logged given configuredLevel.
   */
  private shouldLog = (requestedLevel: LogLevel, configuredLevel: LogLevel): boolean => {
    if (configuredLevel === LogLevel.OFF) {
      return false;
    }
    return requestedLevel >= configuredLevel;
  };

  /**
   * Create a LogEntry with provided data and default fields (component, timestamp).
   */
  private createLogEntry(level: LogLevel, message: string, ...args: any[]): LogEntry {
    return {
      level,
      message,
      component: this.options.component,
      timestamp: new Date().toISOString(),
      args
    }
  }

  /** Set the logger level and return the previous level. */
  setLevel(newLevel: LogLevel): LogLevel {
    if (this.options.immutable) return this.options.level;
    let oldLevel = this.options.level;
    this.options.level = newLevel;
    return oldLevel;
  }

  /** Get current logger level. */
  getLevel(): LogLevel {
    return this.options.level;
  }

  /** Return the logger component name. */
  component(): string {
    return this.options.component;
  }

  /** Return the currently bound appenders. */
  getAppenders(): Appender[] {
    return this.appenders;
  }

  /** Get the threshold configured for the named appender. */
  getThreshold(name: string): LogLevel | undefined {
    return this.appendersThreshold.get(name);
  }

  /**
   * Set the threshold for a named appender. Returns the previous level if present.
   */
  setThreshold(name: string, newLevel: LogLevel): LogLevel | undefined {
    if (this.options.immutable) return this.appendersThreshold.get(name);
    const oldLevel: LogLevel | undefined = this.appendersThreshold.get(name);
    if (oldLevel !== undefined)  this.appendersThreshold.set(name, newLevel);
    return oldLevel;
  }

}
